// ═══════════════════════════════════════════════════════════════════════
// locale.ts — Location awareness + local currency display (display-only)
// ═══════════════════════════════════════════════════════════════════════

export interface CountryInfo {
  code: string;
  name: string;
  flag: string;
  currency: string;
}

// Keyed by ISO-3166 alpha-2 code
export const ISO_COUNTRY_META: Record<string, { name: string; flag: string; currency: string }> = {
  BH: { name: "Bahrain", flag: "🇧🇭", currency: "BHD" },
  SA: { name: "Saudi Arabia", flag: "🇸🇦", currency: "SAR" },
  AE: { name: "United Arab Emirates", flag: "🇦🇪", currency: "AED" },
  QA: { name: "Qatar", flag: "🇶🇦", currency: "QAR" },
  KW: { name: "Kuwait", flag: "🇰🇼", currency: "KWD" },
  OM: { name: "Oman", flag: "🇴🇲", currency: "OMR" },
  EG: { name: "Egypt", flag: "🇪🇬", currency: "EGP" },
  NG: { name: "Nigeria", flag: "🇳🇬", currency: "NGN" },
  KE: { name: "Kenya", flag: "🇰🇪", currency: "KES" },
  ZA: { name: "South Africa", flag: "🇿🇦", currency: "ZAR" },
  MA: { name: "Morocco", flag: "🇲🇦", currency: "MAD" },
  GB: { name: "United Kingdom", flag: "🇬🇧", currency: "GBP" },
  US: { name: "United States", flag: "🇺🇸", currency: "USD" },
  SG: { name: "Singapore", flag: "🇸🇬", currency: "SGD" },
  IN: { name: "India", flag: "🇮🇳", currency: "INR" },
  PK: { name: "Pakistan", flag: "🇵🇰", currency: "PKR" },
  TR: { name: "Turkey", flag: "🇹🇷", currency: "TRY" },
};

// Exported name-keyed view for legacy / string matchers (e.g. DashboardContextStrip)
export const COUNTRY_NAME_META: Record<string, { flag: string; currency: string }> = {
  Bahrain: { flag: "🇧🇭", currency: "BHD" },
  "Saudi Arabia": { flag: "🇸🇦", currency: "SAR" },
  UAE: { flag: "🇦🇪", currency: "AED" },
  "United Arab Emirates": { flag: "🇦🇪", currency: "AED" },
  Qatar: { flag: "🇶🇦", currency: "QAR" },
  Kuwait: { flag: "🇰🇼", currency: "KWD" },
  Oman: { flag: "🇴🇲", currency: "OMR" },
  Egypt: { flag: "🇪🇬", currency: "EGP" },
  Nigeria: { flag: "🇳🇬", currency: "NGN" },
  Kenya: { flag: "🇰🇪", currency: "KES" },
  "South Africa": { flag: "🇿🇦", currency: "ZAR" },
  Morocco: { flag: "🇲🇦", currency: "MAD" },
  "United Kingdom": { flag: "🇬🇧", currency: "GBP" },
  UK: { flag: "🇬🇧", currency: "GBP" },
  "United States": { flag: "🇺🇸", currency: "USD" },
  USA: { flag: "🇺🇸", currency: "USD" },
  Singapore: { flag: "🇸🇬", currency: "SGD" },
  India: { flag: "🇮🇳", currency: "INR" },
  Pakistan: { flag: "🇵🇰", currency: "PKR" },
  Turkey: { flag: "🇹🇷", currency: "TRY" },
};

// Indicative rates against USD (indicative only, refreshed manually, never used for billing)
export const INDICATIVE_USD_RATES: Record<string, number> = {
  USD: 1.0,
  GBP: 0.78,
  EUR: 0.92,
  BHD: 0.376,
  SAR: 3.75,
  AED: 3.67,
  QAR: 3.64,
  KWD: 0.31,
  OMR: 0.385,
  EGP: 48.5,
  NGN: 1550.0,
  KES: 129.0,
  ZAR: 18.2,
  MAD: 9.8,
  SGD: 1.34,
  INR: 83.5,
  PKR: 278.5,
  TRY: 33.0,
};

export const RATES_UPDATED = "2026-07-01";

const STORAGE_KEY = "sloe_locale_override";

export function getLocaleOverride(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setLocaleOverride(iso: string | null): void {
  try {
    if (iso === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, iso.toUpperCase());
    }
  } catch {
    // Safari private mode protection
  }
}

export function detectCountry(): string | null {
  try {
    const langs = Array.from(navigator.languages || [navigator.language]).filter(Boolean);
    for (const lang of langs) {
      const parts = lang.split(/[-_]/);
      if (parts.length >= 2) {
        const region = parts[parts.length - 1].toUpperCase();
        if (region.length === 2 && ISO_COUNTRY_META[region]) {
          return region;
        }
      }
    }
    // Timezone check
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("London")) return "GB";
    if (tz.includes("Johannesburg")) return "ZA";
    if (tz.includes("Lagos")) return "NG";
    if (tz.includes("Nairobi")) return "KE";
    if (tz.includes("Riyadh")) return "SA";
    if (tz.includes("Dubai")) return "AE";
    if (tz.includes("Singapore")) return "SG";
    if (tz.includes("Kolkata") || tz.includes("Calcutta")) return "IN";
  } catch {
    // missing Intl or navigator
  }
  return null;
}

export function getActiveCountry(): CountryInfo | null {
  const override = getLocaleOverride();
  if (override === "NONE") return null;
  if (override && ISO_COUNTRY_META[override]) {
    return { code: override, ...ISO_COUNTRY_META[override] };
  }
  const detected = detectCountry();
  if (detected && ISO_COUNTRY_META[detected]) {
    return { code: detected, ...ISO_COUNTRY_META[detected] };
  }
  return null;
}

export function formatLocalPrice(usdAmount: number, currency: string): string | null {
  if (!currency || currency === "USD") return null;
  const rate = INDICATIVE_USD_RATES[currency];
  if (!rate || isNaN(rate) || !Number.isFinite(rate)) return null;

  const raw = usdAmount * rate;
  let rounded: number;
  if (raw < 100) {
    rounded = Math.round(raw);
  } else if (raw < 1000) {
    rounded = Math.round(raw / 10) * 10;
  } else {
    rounded = Math.round(raw / 100) * 100;
  }

  try {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(rounded);
    return formatted;
  } catch {
    return null;
  }
}
